"use client";

import { a, useSpring, type SpringValue } from "@react-spring/three";
import { Decal, useTexture } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { memo, useEffect, useMemo, useRef, type MutableRefObject, type RefObject } from "react";
import * as THREE from "three";

import type { AlbumCover, VisibleCoverSlot } from "./AlbumCoverTypes";
import {
  FRONT_ANGLE,
  getInversePerspectiveScale,
  getOverlayAngle,
  getOverlayOpacity,
  getRotationIndexFromRotation,
  getShortestAngleDistance,
  roundOverlayValue,
  TAU,
} from "./AlbumCoverGeometry";
import {
  getHeroMusicOverlayPerspective,
  getHeroMusicOverlayScale,
  type HeroMusicVisualControls,
} from "./HeroMusicVisualControls";
import { clampValue } from "./HeroMusicUtils";
import { HERO_MUSIC_TIMELINE_GROUP_IDS, type HeroMusicTimelineGroupId } from "./HeroMusicTimelineBar";

type AnimatedImageVariantScale = SpringValue<number>;
type HtmlOverlayRefs = MutableRefObject<(HTMLDivElement | null)[]>;

type DecalTransform = {
  position: [number, number, number];
  rotation: [number, number, number];
};

type AlbumCoverSceneProps = {
  activeTimelineGroup: HeroMusicTimelineGroupId;
  captureRenderedRotationIndexRef: MutableRefObject<() => number>;
  controls: HeroMusicVisualControls;
  coverSlotCount: number;
  covers: AlbumCover[];
  isDragging: boolean;
  onReady: (readySignature: string) => void;
  overlayCardRefs: HtmlOverlayRefs;
  radius: number;
  readySignature: string;
  rotationY: number;
  visibleCoverSlots: VisibleCoverSlot[];
};

export const OVERLAY_CARD_BACKGROUND = "red";
export const OVERLAY_CARD_BORDER_RADIUS = 24;
export const OVERLAY_CARD_CONTROL_PADDING = 12;
export const OVERLAY_CARD_TIMELINE_LEFT_PADDING = 4;
const OVERLAY_CARD_TIMELINE_RIGHT_PADDING = 38;
const OVERLAY_CARD_VISUAL_ALIGNMENT_OFFSET_Y = 1;
export const PLAY_BUTTON_LINE_GAP = 8;
const PLAY_BUTTON_SIZE = 48;
const PLAY_BUTTON_TIMELINE_OFFSET =
  OVERLAY_CARD_CONTROL_PADDING + PLAY_BUTTON_SIZE + PLAY_BUTTON_LINE_GAP + OVERLAY_CARD_TIMELINE_LEFT_PADDING;
export const PLAY_BUTTON_TIMELINE_MARGIN_BOTTOM = PLAY_BUTTON_SIZE / 2 - 5;

const IMAGE_VARIANT_FADE_SPRING = {
  tension: 170,
  friction: 24,
  precision: 0.001,
};
const IMAGE_VARIANT_INCOMING_SCALE = 0.9875;
const IMAGE_VARIANT_REST_SCALE = 1;
const IMAGE_VARIANT_OUTGOING_SCALE = 1.0125;
const COVER_IMAGE_GRADIENT_START = 0.52;
const COVER_IMAGE_GRADIENT_STRENGTH = 0.25;

function CameraRig({ controls }: { controls: HeroMusicVisualControls }) {
  const camera = useThree(state => state.camera);

  useEffect(() => {
    if (!(camera instanceof THREE.PerspectiveCamera)) return;

    camera.fov = controls.cameraFov;
    camera.zoom = controls.size;
    camera.position.set(0, controls.cameraY, controls.cameraZ);
    camera.updateProjectionMatrix();
  }, [camera, controls.cameraFov, controls.cameraY, controls.cameraZ, controls.size]);

  return null;
}

export const AlbumCoverScene = memo(function AlbumCoverScene({
  activeTimelineGroup,
  captureRenderedRotationIndexRef,
  controls,
  coverSlotCount,
  covers,
  radius,
  rotationY,
  isDragging,
  onReady,
  overlayCardRefs,
  readySignature,
  visibleCoverSlots,
}: AlbumCoverSceneProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const notifiedReadySignatureRef = useRef<string | null>(null);
  const hasUnmountedRef = useRef(false);
  const onReadyRef = useRef(onReady);
  const previousImageVariantGroupRef = useRef(activeTimelineGroup);
  const previousTimelineGroupRef = useRef(activeTimelineGroup);
  const readyFrameIdsRef = useRef<number[]>([]);
  const textureImageUrls = useMemo(
    () => covers.flatMap(cover => HERO_MUSIC_TIMELINE_GROUP_IDS.map(groupId => cover.variants[groupId].imageUrl)),
    [covers],
  );
  const textures = useTexture(textureImageUrls);
  const texturesByImageUrl = useMemo(
    () => new Map(textureImageUrls.map((imageUrl, index) => [imageUrl, textures[index]])),
    [textureImageUrls, textures],
  );
  const visibleCoverSlotSignature = visibleCoverSlots.map(slot => slot.cover.id).join("|");
  const coverAlphaMap = useMemo(
    () => createRoundedCoverAlphaMap(controls.coverBorderRadius, controls.coverWidth, controls.coverHeight),
    [controls.coverBorderRadius, controls.coverHeight, controls.coverWidth],
  );
  const coverGradientTexture = useMemo(() => createCoverImageGradientTexture(), []);

  onReadyRef.current = onReady;

  useEffect(() => {
    return () => {
      coverAlphaMap?.dispose();
      coverGradientTexture?.dispose();
    };
  }, [coverAlphaMap, coverGradientTexture]);

  useEffect(() => {
    hasUnmountedRef.current = false;

    return () => {
      hasUnmountedRef.current = true;
      readyFrameIdsRef.current.forEach(frameId => window.cancelAnimationFrame(frameId));
      readyFrameIdsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (notifiedReadySignatureRef.current === readySignature) return;

    readyFrameIdsRef.current.forEach(frameId => window.cancelAnimationFrame(frameId));
    readyFrameIdsRef.current = [];
  }, [readySignature]);

  const [spring, api] = useSpring(() => ({
    rotationX: controls.carouselTiltX,
    rotationY,
    rotationZ: controls.carouselTiltZ,
    config: getSpringConfig(controls, false),
  }));
  const [imageVariantSpring, imageVariantApi] = useSpring(() => ({
    ...getImageVariantSpringTarget(activeTimelineGroup, activeTimelineGroup),
    config: IMAGE_VARIANT_FADE_SPRING,
  }));

  useEffect(() => {
    captureRenderedRotationIndexRef.current = () => {
      const latestRotationY = spring.rotationY.get();

      api.stop();

      return getRotationIndexFromRotation(latestRotationY, coverSlotCount);
    };
  }, [api, captureRenderedRotationIndexRef, coverSlotCount, spring.rotationY]);

  useEffect(() => {
    const didTimelineGroupChange = previousTimelineGroupRef.current !== activeTimelineGroup;

    previousTimelineGroupRef.current = activeTimelineGroup;
    api.start({
      rotationX: controls.carouselTiltX,
      rotationY,
      rotationZ: controls.carouselTiltZ,
      immediate: isDragging || didTimelineGroupChange,
      config: getSpringConfig(controls, isDragging),
    });
  }, [activeTimelineGroup, rotationY, isDragging, api, controls]);

  useEffect(() => {
    const previousTimelineGroup = previousImageVariantGroupRef.current;
    const didTimelineGroupChange = previousTimelineGroup !== activeTimelineGroup;

    previousImageVariantGroupRef.current = activeTimelineGroup;
    imageVariantApi.start({
      from: didTimelineGroupChange ? getImageVariantSpringStart(previousTimelineGroup, activeTimelineGroup) : undefined,
      ...getImageVariantSpringTarget(previousTimelineGroup, activeTimelineGroup),
      immediate: !didTimelineGroupChange,
      config: IMAGE_VARIANT_FADE_SPRING,
    });
  }, [activeTimelineGroup, imageVariantApi, rotationY, visibleCoverSlotSignature]);

  const decalTransforms = useMemo(
    () => getDecalTransforms(visibleCoverSlots.length, controls, radius),
    [controls, radius, visibleCoverSlots.length],
  );

  useFrame(() => {
    if (notifiedReadySignatureRef.current === readySignature) return;
    if (decalTransforms.length === 0) return;

    notifiedReadySignatureRef.current = readySignature;
    const scheduledReadySignature = readySignature;
    const outerFrameId = window.requestAnimationFrame(() => {
      if (hasUnmountedRef.current) return;
      if (notifiedReadySignatureRef.current !== scheduledReadySignature) return;

      const innerFrameId = window.requestAnimationFrame(() => {
        if (hasUnmountedRef.current) return;
        if (notifiedReadySignatureRef.current !== scheduledReadySignature) return;

        readyFrameIdsRef.current = [];
        onReadyRef.current(scheduledReadySignature);
      });

      readyFrameIdsRef.current = [innerFrameId];
    });

    readyFrameIdsRef.current = [outerFrameId];
  });

  return (
    <>
      <CameraRig controls={controls} />
      <HtmlOverlayCards
        controls={controls}
        coverCount={decalTransforms.length}
        meshRef={meshRef}
        overlayCardRefs={overlayCardRefs}
      />
      <a.mesh ref={meshRef} rotation-x={spring.rotationX} rotation-y={spring.rotationY} rotation-z={spring.rotationZ}>
        <cylinderGeometry args={[radius, radius, 2, 128]} />
        <meshStandardMaterial opacity={0} transparent />
        {decalTransforms.map((decal, positionIndex) => {
          const slotIndex = getCoverIndexFromPositionIndex(positionIndex, visibleCoverSlots.length);
          const slot = visibleCoverSlots[slotIndex];

          if (!slot) return null;

          const genreTexture = texturesByImageUrl.get(slot.cover.variants.genres.imageUrl);
          const languageTexture = texturesByImageUrl.get(slot.cover.variants.languages.imageUrl);

          if (!genreTexture || !languageTexture) return null;

          return (
            <AlbumCoverDecal
              alphaMap={coverAlphaMap}
              controls={controls}
              decal={decal}
              genreBlend={imageVariantSpring.genreBlend}
              genreScale={imageVariantSpring.genreScale}
              genreTexture={genreTexture}
              gradientTexture={coverGradientTexture}
              key={slot.cover.id}
              languageBlend={imageVariantSpring.languageBlend}
              languageScale={imageVariantSpring.languageScale}
              languageTexture={languageTexture}
            />
          );
        })}
      </a.mesh>
    </>
  );
});

AlbumCoverScene.displayName = "AlbumCoverScene";

function getDecalTransforms(count: number, controls: HeroMusicVisualControls, radius: number) {
  const transforms: DecalTransform[] = [];

  for (let i = 0; i < count; i++) {
    const angle = (i / count) * TAU + FRONT_ANGLE + controls.coverAngleOffset;
    const positionRadius = radius + controls.coverRadiusOffset;
    const x = Math.cos(angle) * positionRadius;
    const z = Math.sin(angle) * positionRadius;
    const y = controls.coverOffsetY;
    const position = new THREE.Vector3(x, y, z);
    const target = position.clone().multiplyScalar(radius);
    const up = new THREE.Vector3(0, 1, 0);
    const matrix = new THREE.Matrix4().lookAt(target, position, up);
    const rotation = new THREE.Euler().setFromRotationMatrix(matrix);

    rotation.z += controls.coverRotationZ;

    transforms.push({
      position: [x, y, z],
      rotation: [rotation.x, rotation.y, rotation.z],
    });
  }

  return transforms;
}

function getCoverIndexFromPositionIndex(positionIndex: number, count: number) {
  return (count - positionIndex) % count;
}

function AlbumCoverDecal({
  alphaMap,
  controls,
  decal,
  genreBlend,
  genreScale,
  genreTexture,
  gradientTexture,
  languageBlend,
  languageScale,
  languageTexture,
}: {
  alphaMap: THREE.CanvasTexture | null;
  controls: HeroMusicVisualControls;
  decal: DecalTransform;
  genreBlend: AnimatedImageVariantScale;
  genreScale: AnimatedImageVariantScale;
  genreTexture: THREE.Texture;
  gradientTexture: THREE.CanvasTexture | null;
  languageBlend: AnimatedImageVariantScale;
  languageScale: AnimatedImageVariantScale;
  languageTexture: THREE.Texture;
}) {
  const genreMaterialRef = useRef<THREE.MeshBasicMaterial>(null);
  const languageMaterialRef = useRef<THREE.MeshBasicMaterial>(null);
  const genreDisplayTexture = useMemo(() => createImageVariantTexture(genreTexture), [genreTexture]);
  const languageDisplayTexture = useMemo(() => createImageVariantTexture(languageTexture), [languageTexture]);

  useEffect(() => {
    return () => {
      genreDisplayTexture.dispose();
      languageDisplayTexture.dispose();
    };
  }, [genreDisplayTexture, languageDisplayTexture]);

  useFrame(() => {
    setImageVariantMaterialFrame(
      genreMaterialRef.current,
      genreDisplayTexture,
      genreBlend.get(),
      genreScale.get(),
      controls,
    );
    setImageVariantMaterialFrame(
      languageMaterialRef.current,
      languageDisplayTexture,
      languageBlend.get(),
      languageScale.get(),
      controls,
    );
  });

  return (
    <>
      <Decal
        position={decal.position}
        renderOrder={-1}
        rotation={decal.rotation}
        scale={[controls.coverWidth, controls.coverHeight, 1]}
      >
        <meshBasicMaterial
          alphaMap={alphaMap}
          alphaTest={0.01}
          color={controls.coverBackfaceColor}
          depthTest={false}
          depthWrite={false}
          opacity={controls.coverOpacity}
          side={THREE.DoubleSide}
          transparent
        />
      </Decal>
      <Decal
        position={decal.position}
        renderOrder={0}
        rotation={decal.rotation}
        scale={[controls.coverWidth, controls.coverHeight, 1]}
      >
        <meshBasicMaterial
          ref={genreMaterialRef}
          alphaMap={alphaMap}
          alphaTest={0.01}
          depthTest={false}
          depthWrite={false}
          map={genreDisplayTexture}
          opacity={controls.coverOpacity * genreBlend.get()}
          side={THREE.FrontSide}
          transparent
        />
      </Decal>
      <Decal
        position={decal.position}
        renderOrder={1}
        rotation={decal.rotation}
        scale={[controls.coverWidth, controls.coverHeight, 1]}
      >
        <meshBasicMaterial
          ref={languageMaterialRef}
          alphaMap={alphaMap}
          alphaTest={0.01}
          depthTest={false}
          depthWrite={false}
          map={languageDisplayTexture}
          opacity={controls.coverOpacity * languageBlend.get()}
          side={THREE.FrontSide}
          transparent
        />
      </Decal>
      {gradientTexture && (
        <Decal
          position={decal.position}
          renderOrder={2}
          rotation={decal.rotation}
          scale={[controls.coverWidth, controls.coverHeight, 1]}
        >
          <meshBasicMaterial
            alphaMap={alphaMap}
            alphaTest={0.01}
            depthTest={false}
            depthWrite={false}
            map={gradientTexture}
            opacity={controls.coverOpacity}
            side={THREE.FrontSide}
            transparent
          />
        </Decal>
      )}
    </>
  );
}

function HtmlOverlayCards({
  controls,
  coverCount,
  meshRef,
  overlayCardRefs,
}: {
  controls: HeroMusicVisualControls;
  coverCount: number;
  meshRef: RefObject<THREE.Mesh | null>;
  overlayCardRefs: HtmlOverlayRefs;
}) {
  const { size } = useThree();

  useFrame(() => {
    const mesh = meshRef.current;

    if (!mesh) return;

    const overlayPerspective = getHeroMusicOverlayPerspective(controls, size.height);
    const overlayScale = getHeroMusicOverlayScale(overlayPerspective, controls.cameraZ);

    for (let positionIndex = 0; positionIndex < coverCount; positionIndex++) {
      const element = overlayCardRefs.current[getCoverIndexFromPositionIndex(positionIndex, coverCount)];

      if (!element) continue;

      const angle = getOverlayAngle(mesh.rotation.y, positionIndex, coverCount, controls.coverAngleOffset);
      const isInFrontHemisphere = Math.sin(angle) > 0;
      const opacity = getOverlayOpacity(angle);

      if (!isInFrontHemisphere) {
        element.style.opacity = "0";
        element.style.setProperty("--overlay-card-pointer-events", "none");
        element.style.setProperty("--overlay-play-pointer-events", "none");
        element.style.visibility = "hidden";
        continue;
      }

      const radius = (controls.radius + controls.coverRadiusOffset) * overlayScale;
      const x = Math.cos(angle) * radius;
      const y = (controls.cameraY - controls.coverOffsetY) * overlayScale + OVERLAY_CARD_VISUAL_ALIGNMENT_OFFSET_Y;
      const z = Math.sin(angle) * radius;
      const cardRotationY = -getShortestAngleDistance(angle, FRONT_ANGLE);
      const inversePerspectiveScale = getInversePerspectiveScale(overlayPerspective, z);
      const timelineWidth =
        element.offsetWidth / inversePerspectiveScale -
        (OVERLAY_CARD_TIMELINE_RIGHT_PADDING + PLAY_BUTTON_TIMELINE_OFFSET);

      element.style.opacity = `${roundOverlayValue(opacity)}`;
      element.style.setProperty("--overlay-card-pointer-events", "auto");
      element.style.setProperty("--overlay-play-pointer-events", opacity > 0.5 ? "auto" : "none");
      element.style.setProperty("--overlay-card-inverse-scale", `${roundOverlayValue(inversePerspectiveScale)}`);
      element.style.setProperty("--overlay-card-counter-rotation-y", `${roundOverlayValue(-cardRotationY)}rad`);
      element.style.setProperty(
        "--overlay-card-counter-rotation-z",
        `${roundOverlayValue(-controls.coverRotationZ)}rad`,
      );
      element.style.setProperty("--overlay-timeline-width", `${roundOverlayValue(Math.max(0, timelineWidth))}px`);
      element.style.transform = `translate3d(calc(-50% + ${roundOverlayValue(x)}px), calc(-50% + ${roundOverlayValue(
        y,
      )}px), ${roundOverlayValue(z)}px) rotateY(${roundOverlayValue(cardRotationY)}rad) rotate(${roundOverlayValue(
        controls.coverRotationZ,
      )}rad)`;
      element.style.visibility = "visible";
      element.style.zIndex = `${Math.round(10_000 + z)}`;
    }
  });

  return null;
}

function getImageVariantSpringStart(previousGroupId: HeroMusicTimelineGroupId, groupId: HeroMusicTimelineGroupId) {
  const incomingScale = getImageVariantIncomingScale(previousGroupId, groupId);

  switch (groupId) {
    case "genres":
      return {
        genreBlend: 0,
        genreScale: incomingScale,
        languageBlend: 1,
        languageScale: IMAGE_VARIANT_REST_SCALE,
      };
    case "languages":
      return {
        genreBlend: 1,
        genreScale: IMAGE_VARIANT_REST_SCALE,
        languageBlend: 0,
        languageScale: incomingScale,
      };
  }
}

function getImageVariantSpringTarget(previousGroupId: HeroMusicTimelineGroupId, groupId: HeroMusicTimelineGroupId) {
  const outgoingScale = getImageVariantOutgoingScale(previousGroupId, groupId);

  switch (groupId) {
    case "genres":
      return {
        genreBlend: 1,
        genreScale: IMAGE_VARIANT_REST_SCALE,
        languageBlend: 0,
        languageScale: outgoingScale,
      };
    case "languages":
      return {
        genreBlend: 0,
        genreScale: outgoingScale,
        languageBlend: 1,
        languageScale: IMAGE_VARIANT_REST_SCALE,
      };
  }
}

function getImageVariantIncomingScale(previousGroupId: HeroMusicTimelineGroupId, groupId: HeroMusicTimelineGroupId) {
  if (isImageVariantScaleBackTransition(previousGroupId, groupId)) return IMAGE_VARIANT_OUTGOING_SCALE;

  return IMAGE_VARIANT_INCOMING_SCALE;
}

function getImageVariantOutgoingScale(previousGroupId: HeroMusicTimelineGroupId, groupId: HeroMusicTimelineGroupId) {
  if (isImageVariantScaleBackTransition(previousGroupId, groupId)) return IMAGE_VARIANT_INCOMING_SCALE;

  return IMAGE_VARIANT_OUTGOING_SCALE;
}

function isImageVariantScaleBackTransition(
  previousGroupId: HeroMusicTimelineGroupId,
  groupId: HeroMusicTimelineGroupId,
) {
  return previousGroupId === "languages" && groupId === "genres";
}

function createImageVariantTexture(texture: THREE.Texture) {
  const imageVariantTexture = texture.clone();

  imageVariantTexture.center.set(0.5, 0.5);
  imageVariantTexture.wrapS = THREE.ClampToEdgeWrapping;
  imageVariantTexture.wrapT = THREE.ClampToEdgeWrapping;

  return imageVariantTexture;
}

function createCoverImageGradientTexture() {
  if (typeof document === "undefined") return null;

  const width = 16;
  const height = 256;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = width;
  canvas.height = height;

  if (!context) return null;

  const gradient = context.createLinearGradient(0, 0, 0, height);

  gradient.addColorStop(0, "rgba(0, 0, 0, 0)");
  gradient.addColorStop(COVER_IMAGE_GRADIENT_START, "rgba(0, 0, 0, 0)");
  gradient.addColorStop(1, `rgba(0, 0, 0, ${COVER_IMAGE_GRADIENT_STRENGTH})`);

  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);

  const texture = new THREE.CanvasTexture(canvas);

  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;

  return texture;
}

function setImageVariantMaterialFrame(
  material: THREE.MeshBasicMaterial | null,
  texture: THREE.Texture,
  blend: number,
  imageScale: number,
  controls: HeroMusicVisualControls,
) {
  setImageVariantTextureScale(texture, imageScale);

  if (material) {
    material.opacity = controls.coverOpacity * blend;
  }
}

function setImageVariantTextureScale(texture: THREE.Texture, imageScale: number) {
  const repeat = 1 / Math.max(0.001, imageScale);

  texture.repeat.set(repeat, repeat);
  texture.updateMatrix();
}

function createRoundedCoverAlphaMap(borderRadius: number, coverWidth: number, coverHeight: number) {
  if (typeof document === "undefined") return null;

  const maxTextureSize = 512;
  const maxCoverSize = Math.max(coverWidth, coverHeight);
  const width = Math.max(1, Math.round((coverWidth / maxCoverSize) * maxTextureSize));
  const height = Math.max(1, Math.round((coverHeight / maxCoverSize) * maxTextureSize));
  const radius = Math.min(width, height) * clampValue(borderRadius, 0, 0.5);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = width;
  canvas.height = height;

  if (!context) return null;

  context.fillStyle = "black";
  context.fillRect(0, 0, width, height);
  context.fillStyle = "white";
  context.beginPath();
  context.roundRect(0, 0, width, height, radius);
  context.fill();

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;

  return texture;
}

function getSpringConfig(controls: HeroMusicVisualControls, isDragging: boolean) {
  if (isDragging) {
    return {
      tension: controls.dragTension,
      friction: controls.dragFriction,
    };
  }

  return {
    mass: 1,
    tension: controls.springTension,
    friction: controls.springFriction,
    precision: 0.0001,
  };
}
